import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, FileDown, Send, CheckCircle2, XCircle, Briefcase, Loader2,
  Building2, Shield, Car, Percent, Pencil, Save, X, User,
} from "lucide-react";

const statusColor = {
  draft: "bg-slate-500/20 text-slate-300 border-slate-500/30",
  sent: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  approved: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  paid: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  overdue: "bg-red-500/20 text-red-300 border-red-500/30",
  cancelled: "bg-slate-700/40 text-slate-500 border-slate-700/40",
};

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [company, setCompany] = useState(null);
  const [busy, setBusy] = useState(false);
  // Iter 46: inline-edit state for lines + customer/recipient names
  const [editMode, setEditMode] = useState(false);
  const [draftLines, setDraftLines] = useState([]);
  const [draftRecipient, setDraftRecipient] = useState("");
  const [draftCustomer, setDraftCustomer] = useState("");
  const [draftTaxPct, setDraftTaxPct] = useState(0);

  const load = () => api.get(`/invoices/${id}`).then((r) => setInv(r.data));
  useEffect(() => {
    load();
    // Iter 47: pull company profile so the preview shows the real address
    api.get("/setup/company-profile").then((r) => setCompany(r.data)).catch(() => {});
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = () => {
    setDraftLines((inv.lines || []).map((l) => ({
      description: l.description || "",
      code: l.code || "",
      quantity: Number(l.quantity ?? 1),
      unit_price: Number(l.unit_price ?? 0),
      discount_pct: Number(l.discount_pct ?? 0),
    })));
    setDraftRecipient(inv.recipient_name || "");
    setDraftCustomer(inv.customer_name || "");
    setDraftTaxPct(Number(inv.tax_pct ?? 0));
    setEditMode(true);
  };

  const cancelEdit = () => { setEditMode(false); setDraftLines([]); };

  const updateLine = (idx, field, value) => {
    setDraftLines((ls) => ls.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };

  const previewTotals = useMemo(() => {
    const lines = draftLines.map((l) => {
      const q = Number(l.quantity) || 0;
      const up = Number(l.unit_price) || 0;
      const d = Number(l.discount_pct) || 0;
      // Iter 46: return NUMBERS (not strings from <input type=number>) so the
      // backend's arithmetic (subtotal/tax) doesn't crash with TypeError.
      return {
        description: String(l.description || ""),
        code: l.code || null,
        quantity: q,
        unit_price: up,
        discount_pct: d,
        amount: +(q * up * (1 - d / 100)).toFixed(2),
      };
    });
    const subtotal = +lines.reduce((s, l) => s + l.amount, 0).toFixed(2);
    const tax = +(subtotal * (Number(draftTaxPct) / 100)).toFixed(2);
    return { lines, subtotal, tax, total: +(subtotal + tax).toFixed(2) };
  }, [draftLines, draftTaxPct]);

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/invoices/${id}`, {
        lines: previewTotals.lines,
        recipient_name: draftRecipient,
        customer_name: draftCustomer,
        tax_pct: Number(draftTaxPct),
      });
      toast.success("Invoice atualizada");
      setEditMode(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/invoices/${id}/status`, { status });
      toast.success(`Marked ${status}`);
      load();
    } finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${inv.invoice_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch { toast.error("PDF failed"); }
  };

  const adjustDiscount = async () => {
    const current = inv.lines?.[0]?.discount_pct ?? inv.body_shop_commission_pct ?? 25;
    const raw = window.prompt(
      `Body shop wholesale discount %\n\nApplies across all lines. Body shop pays subtotal, re-bills insurance at retail.\n\nCurrent: ${current}%`,
      String(current),
    );
    if (raw === null) return;
    const parsed = parseFloat(String(raw).replace(",", "."));
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) {
      toast.error("Invalid percentage (0–100)");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/invoices/${id}/discount`, { discount_pct: parsed });
      toast.success(`Discount set to ${parsed}%`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (!inv) {
    return <div className="text-slate-500 p-8 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>;
  }

  const TypeIcon = inv.type === "body_shop" ? Building2 : Shield;

  return (
    <div className="space-y-6 animate-slide-up max-w-5xl" data-testid="invoice-detail-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <button onClick={() => navigate("/billing")} className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold hover:text-slate-300 flex items-center gap-1 mb-2">
            <ArrowLeft className="w-3 h-3" /> Billing
          </button>
          <h1 className="font-display text-4xl font-semibold tracking-tight font-mono" data-testid="invoice-number">{inv.invoice_number}</h1>
          <div className="flex items-center gap-3 text-xs text-slate-400 mt-2">
            <Badge className={`${statusColor[inv.status]} border text-[10px] uppercase tracking-wider`}>{inv.status}</Badge>
            <span className="flex items-center gap-1"><TypeIcon className="w-3 h-3" />{inv.type.replace("_", " ")}</span>
            <span>Issued {inv.issued_date || (inv.created_at || "").slice(0, 10)}</span>
            {inv.due_date && <span>Due {inv.due_date}</span>}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="border-slate-700" onClick={downloadPdf} data-testid="download-pdf-btn">
            <FileDown className="w-3.5 h-3.5 mr-1.5" /> Download PDF
          </Button>
          {!editMode && inv.status !== "paid" && inv.status !== "cancelled" && (
            <Button variant="outline" className="border-sky-500/40 text-sky-300 hover:bg-sky-500/10" onClick={startEdit} disabled={busy} data-testid="edit-invoice-btn">
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> Editar Valores
            </Button>
          )}
          {editMode && (
            <>
              <Button className="bg-emerald-600 hover:bg-emerald-500" onClick={saveEdit} disabled={busy} data-testid="save-invoice-btn">
                <Save className="w-3.5 h-3.5 mr-1.5" /> Salvar
              </Button>
              <Button variant="outline" className="border-slate-700" onClick={cancelEdit} disabled={busy} data-testid="cancel-edit-btn">
                <X className="w-3.5 h-3.5 mr-1.5" /> Cancelar
              </Button>
            </>
          )}
          {inv.type === "body_shop" && inv.status !== "paid" && inv.status !== "cancelled" && !editMode && (
            <Button variant="outline" className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10" onClick={adjustDiscount} disabled={busy} data-testid="adjust-discount-btn">
              <Percent className="w-3.5 h-3.5 mr-1.5" /> Adjust Discount
            </Button>
          )}
          {inv.status === "draft" && (
            <Button onClick={() => setStatus("sent")} disabled={busy} className="bg-amber-600 hover:bg-amber-500" data-testid="mark-sent-btn">
              <Send className="w-3.5 h-3.5 mr-1.5" /> Mark as Sent
            </Button>
          )}
          {inv.status !== "paid" && inv.status !== "cancelled" && (
            <Button onClick={() => setStatus("paid")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500" data-testid="mark-paid-btn">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Mark as Paid
            </Button>
          )}
          {inv.status === "draft" && (
            <Button onClick={() => setStatus("cancelled")} disabled={busy} variant="outline" className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10">
              <XCircle className="w-3.5 h-3.5 mr-1.5" /> Cancel
            </Button>
          )}
        </div>
      </div>

      {/* Preview matching the PDF layout */}
      <div className="rounded-xl border border-slate-800 bg-white text-slate-900 p-8" data-testid="invoice-preview">
        <div className="flex items-start justify-between gap-6 mb-6">
          <div>
            <img src="/assets/trueline-logo.png" alt="" className="w-16 h-16 object-contain mb-2" onError={(e) => (e.target.style.display = "none")} />
            <div className="font-bold text-base">{company?.name || "⚠ Configure company in Settings"}</div>
            <div className="text-xs text-slate-500 mt-1">
              {[
                company?.address,
                [company?.city, company?.state].filter(Boolean).join(", "),
                company?.zip_code,
              ].filter(Boolean).join(" · ") || "⚠ Missing address"}
            </div>
            <div className="text-xs text-slate-500">{company?.phone || ""}</div>
            <div className="text-xs text-slate-500">{company?.email || ""}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">INVOICE</div>
            <div className="font-mono text-xl font-bold" data-testid="preview-number">{inv.invoice_number}</div>
            <div className="text-xs text-slate-500 mt-3 space-y-0.5">
              <div>RO: <span className="font-mono">{inv.ro_number || "—"}</span></div>
              <div>Issued: <span className="font-mono">{inv.issued_date || "—"}</span></div>
              <div>Due: <span className="font-mono">{inv.due_date || "—"}</span></div>
            </div>
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Total</div>
              <div className="text-3xl font-bold text-sky-600 font-mono" data-testid="preview-total">
                ${(editMode ? previewTotals.total : inv.total).toFixed(2)}
              </div>
            </div>
          </div>
        </div>

        {/* Iter 46: grid now shows 3 columns — Bill To (payer), Vehicle Owner (customer), and Vehicle */}
        <div className="grid grid-cols-3 gap-4 mb-6 bg-slate-50 rounded-lg p-4 border border-slate-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1 flex items-center gap-1">
              <Building2 className="w-3 h-3" /> Bill To {inv.type === "insurance" ? "(Seguradora)" : inv.type === "body_shop" ? "(Body Shop)" : "(Cliente)"}
            </div>
            {editMode ? (
              <input
                type="text"
                value={draftRecipient}
                onChange={(e) => setDraftRecipient(e.target.value)}
                className="w-full font-bold border-b border-sky-400 bg-transparent focus:outline-none px-1 py-0.5"
                data-testid="edit-recipient-name"
              />
            ) : (
              <div className="font-bold" data-testid="preview-recipient">{inv.recipient_name}</div>
            )}
            {inv.recipient_address && <div className="text-xs text-slate-600 mt-1 whitespace-pre-line">{inv.recipient_address}</div>}
            {inv.recipient_phone && <div className="text-xs text-slate-600">{inv.recipient_phone}</div>}
            {inv.recipient_email && <div className="text-xs text-slate-600">{inv.recipient_email}</div>}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1 flex items-center gap-1">
              <User className="w-3 h-3" /> Cliente (Dono do Veículo)
            </div>
            {editMode ? (
              <input
                type="text"
                value={draftCustomer}
                onChange={(e) => setDraftCustomer(e.target.value)}
                className="w-full font-bold border-b border-sky-400 bg-transparent focus:outline-none px-1 py-0.5"
                placeholder="Nome do cliente"
                data-testid="edit-customer-name"
              />
            ) : (
              <div className="font-bold" data-testid="preview-customer">
                {(() => {
                  // Iter 47 (Issue 11): never render the body shop OR insurer name
                  // in the Vehicle Owner slot — that was the Maserati bug.
                  const name = (inv.customer_name || "").trim();
                  const bs = (inv.body_shop_name || inv.recipient_name || "").trim().toLowerCase();
                  const ins = (inv.insurance_company || "").trim().toLowerCase();
                  const invalid = !name
                    || (bs && name.toLowerCase() === bs)
                    || (ins && name.toLowerCase() === ins);
                  if (invalid) {
                    return (
                      <span className="text-amber-600 font-normal text-xs">
                        ⚠ Unknown — click Editar to set vehicle owner
                      </span>
                    );
                  }
                  return name;
                })()}
              </div>
            )}
            {inv.customer_phone && <div className="text-xs text-slate-600 mt-1">{inv.customer_phone}</div>}
            {inv.customer_email && <div className="text-xs text-slate-600">{inv.customer_email}</div>}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1 flex items-center gap-1">
              <Car className="w-3 h-3" /> Veículo
            </div>
            <div className="font-bold">{inv.vehicle_year} {inv.vehicle_make} {inv.vehicle_model}</div>
            {inv.vehicle_vin && <div className="text-xs text-slate-600 font-mono mt-1">VIN: {inv.vehicle_vin}</div>}
          </div>
        </div>

        <div className="text-sm font-bold text-sky-600 mb-2">
          {inv.type === "body_shop" ? "ADAS OPERATIONS" : "CALIBRATION SERVICES"}
        </div>
        <table className="w-full mb-6">
          <thead>
            <tr className="bg-slate-900 text-white text-[10px] uppercase tracking-wider">
              <th className="text-left px-3 py-2">Description</th>
              <th className="text-left px-3 py-2 w-20">Code</th>
              <th className="text-right px-3 py-2 w-24">Price</th>
              <th className="text-right px-3 py-2 w-16">Qty/Hr</th>
              <th className="text-right px-3 py-2 w-14">Dis %</th>
              <th className="text-right px-3 py-2 w-24">Total</th>
            </tr>
          </thead>
          <tbody>
            {(editMode ? previewTotals.lines : inv.lines).map((ln, i) => (
              <tr key={i} className={`border-b border-slate-200 text-sm ${i % 2 ? "bg-slate-50" : "bg-white"}`}>
                <td className="px-3 py-2">
                  {editMode ? (
                    <input
                      type="text"
                      value={draftLines[i]?.description ?? ""}
                      onChange={(e) => updateLine(i, "description", e.target.value)}
                      className="w-full border-b border-sky-400 bg-transparent focus:outline-none px-1 py-0.5"
                      data-testid={`edit-line-desc-${i}`}
                    />
                  ) : ln.description}
                </td>
                <td className="px-3 py-2 text-xs font-mono text-slate-500">{ln.code || "—"}</td>
                <td className="px-3 py-2 text-right font-mono">
                  {editMode ? (
                    <input
                      type="number" step="0.01" min="0"
                      value={draftLines[i]?.unit_price ?? 0}
                      onChange={(e) => updateLine(i, "unit_price", e.target.value)}
                      className="w-24 text-right border-b border-sky-400 bg-transparent focus:outline-none px-1 py-0.5"
                      data-testid={`edit-line-price-${i}`}
                    />
                  ) : `$${(ln.unit_price ?? 0).toFixed(2)}`}
                </td>
                <td className="px-3 py-2 text-right font-mono">
                  {editMode ? (
                    <input
                      type="number" step="0.5" min="0"
                      value={draftLines[i]?.quantity ?? 1}
                      onChange={(e) => updateLine(i, "quantity", e.target.value)}
                      className="w-16 text-right border-b border-sky-400 bg-transparent focus:outline-none px-1 py-0.5"
                      data-testid={`edit-line-qty-${i}`}
                    />
                  ) : ln.quantity}
                </td>
                <td className="px-3 py-2 text-right font-mono">
                  {editMode ? (
                    <input
                      type="number" step="1" min="0" max="100"
                      value={draftLines[i]?.discount_pct ?? 0}
                      onChange={(e) => updateLine(i, "discount_pct", e.target.value)}
                      className="w-14 text-right border-b border-sky-400 bg-transparent focus:outline-none px-1 py-0.5"
                      data-testid={`edit-line-disc-${i}`}
                    />
                  ) : `${ln.discount_pct || 0}%`}
                </td>
                <td className="px-3 py-2 text-right font-mono font-bold text-emerald-600" data-testid={`line-amount-${i}`}>
                  ${(ln.amount ?? 0).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-end">
          <div className="w-64 space-y-1">
            <div className="flex justify-between text-sm">
              <span>Subtotal</span>
              <span className="font-mono">${(editMode ? previewTotals.subtotal : inv.subtotal).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm items-center">
              <span>Tax {editMode ? (
                <input
                  type="number" step="0.1" min="0" max="30"
                  value={draftTaxPct}
                  onChange={(e) => setDraftTaxPct(e.target.value)}
                  className="w-12 text-right border-b border-sky-400 bg-transparent focus:outline-none px-1"
                  data-testid="edit-tax-pct"
                />
              ) : (inv.tax_pct || 0)}%</span>
              <span className="font-mono">${(editMode ? previewTotals.tax : inv.tax).toFixed(2)}</span>
            </div>
            <div className="flex justify-between border-t-2 border-slate-900 pt-2 mt-1 text-lg font-bold">
              <span>TOTAL</span>
              <span className="font-mono text-emerald-600" data-testid="preview-grand-total">
                ${(editMode ? previewTotals.total : inv.total).toFixed(2)}
              </span>
            </div>
            {editMode && inv.type === "insurance" && (
              <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2">
                💡 Dica: seguradoras pagam retail. Body Shop tem desconto wholesale — edite cada invoice separadamente.
              </div>
            )}
          </div>
        </div>

        {inv.notes && (
          <div className="mt-6 pt-4 border-t border-slate-200">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Notes</div>
            <div className="text-xs text-slate-600">{inv.notes}</div>
          </div>
        )}
      </div>

      {inv.job_id && (
        <Link to={`/jobs/${inv.job_id}`}>
          <Button variant="outline" className="border-slate-700">
            <Briefcase className="w-3.5 h-3.5 mr-1.5" /> View Linked Job
          </Button>
        </Link>
      )}
    </div>
  );
}
